import React, { useState } from "react";
import { Col, Button, OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { Link, useParams } from "react-router-dom";
import { ErrorModalComponent } from "@me-pos/error-modal";
import { Pagination, TableHeader, WithRoleAccess } from "@services/ui-components";
import VioletBadge from "@services/ui-components/src/VioletBadge/VioletBadge";
import CreatePositionModal from "../ui-components/CreatePositionModal/CreatePositionModal";
import EditPositionMoadal from "../ui-components/EditPositionModal/EditPositionModal";
import DeletePositionModal from "../ui-components/DeletePositionmodal/DeletePositionModal";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import ErrorService from "../../services/ErrorService";
import ToastComponent from "../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../hooks/useToast";
import {
    useGetCompanyIdQuery,
    useGetEmployeesPositionsTableQuery,
} from "@me-team/host/main/employeeList/graphql/employeeList.hooks";

import { CompanyAccess, EmployeePositionProxy } from "@me-team/host/main/graphql/types";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import useScrollToTopOnChange from "../../hooks/useScrollToTopOnChange";

const Positions: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [showPositionModal, setShowPositionModal] = useState(false);
    const [showEditPositionModal, setEditShowPositionModal] = useState(false);
    const [selecteedPosition, setSelectedPosition] = useState(null);
    const [showDeletePositionModal, setDeletePositionhowPositionModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(null);

    useScrollToTopOnChange(currentPage);

    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const canEditPosition = useRoleAccess(CompanyAccess.PositionEdit);
    const canDeletePosition = useRoleAccess(CompanyAccess.PositionDelete);
    const canOpenEmployeesList = useRoleAccess(CompanyAccess.EmployeeListView);
    const hasPositionActions = canEditPosition || canDeletePosition;

    const {
        data: poss,
        loading,
        refetch,
    } = useGetEmployeesPositionsTableQuery({
        variables: { page: currentPage, items: itemsPerPage, companyId: +currentCompanyId, branchId: +branchId },
        skip: !currentCompanyId,
        onError: (error) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
        context: {
            errorType: "global",
        },
    });
    const { data: companyId, loading: loadingCompanyId } = useGetCompanyIdQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        onError: (error) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
        context: {
            errorType: "global",
        },
    });

    const pageCount = poss?.user.company.employeePositions.pageCount;
    const totalItems = poss?.user.company.employeePositions.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);

    const positions = poss?.user.company.employeePositions.employeePositions;

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const handlePositionModal = () => setShowPositionModal((prevState) => !prevState);

    const editPosition = (employee: EmployeePositionProxy) => {
        setSelectedPosition(employee);
        setEditShowPositionModal((prevState) => !prevState);
    };

    const deletePosition = (employee: any) => {
        if (employee) {
            setSelectedPosition(employee);
        }

        setDeletePositionhowPositionModal((prevState) => !prevState);
    };

    if (loading || loadingCompanyId) return <Spinner />;

    return (
        <>
            <VioletBadge
                text={t(
                    "On this page, you can create new positions, as well as edit and delete existing positions in your company."
                )}
            />
            <TableHeader
                title={t("Positions")}
                paths={[
                    { path: `/positions/${branchId}`, label: t("Personnel") },
                    { path: `/positions/${branchId}`, label: t("Positions") },
                ]}
                buttons={
                    <Col className="text-end">
                        <WithRoleAccess permission={CompanyAccess.PositionCreate}>
                            <Button
                                variant="primary"
                                className="fs-7 fw-bold btn-primary custom-btn mt-3 mt-lg-0"
                                onClick={handlePositionModal}
                            >
                                <i className="bi bi-plus-lg me-1"></i>
                                {t("Add position")}
                            </Button>
                        </WithRoleAccess>
                    </Col>
                }
                showFilters={false}
            ></TableHeader>
            <div className="table-responsive scrollbar">
                <table className="table">
                    <thead>
                        <tr className="fs-7">
                            <th className="px-4">{t("Position")}</th>
                            <th> {t("Employees")} </th>
                            <th>{t("Description")}</th>
                            <th>
                                {t("Specialization")}{" "}
                                <OverlayTrigger
                                    key={"bottom"}
                                    placement={"bottom"}
                                    overlay={
                                        <Popover id="popover-contained" className="text-popover ">
                                            <Popover.Body>
                                                {t(
                                                    "The data in this column is automatically collected from all employees with a specific job title."
                                                )}
                                            </Popover.Body>
                                        </Popover>
                                    }
                                >
                                    <i className="bi bi-info-circle " style={{ padding: "0 1px" }}>
                                        {" "}
                                    </i>
                                </OverlayTrigger>
                            </th>
                            {hasPositionActions ? <th> </th> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {positions?.map((i) => (
                            <tr key={i?.id}>
                                <td className="align-middle px-4">{i.name}</td>
                                <td className="align-middle">
                                    {canOpenEmployeesList ? (
                                        <Link to={`/employees-list/${i?.id}/${branchId}`}>{i?.employees.length}</Link>
                                    ) : (
                                        <span>{i?.employees.length}</span>
                                    )}
                                </td>
                                <td className="align-middle" style={{ width: "400px" }}>
                                    <Col className="p-2">{i?.description || <small> — </small>}</Col>
                                </td>
                                <td className="align-middle" style={{ width: "400px" }}>
                                    <Col className="p-2">
                                        {Array.from(
                                            new Set(
                                                i?.employees.map((employee) => employee.specialization).filter(Boolean)
                                            )
                                        ).join("; ") || <small> — </small>}
                                    </Col>
                                </td>
                                {hasPositionActions ? (
                                    <td className="align-middle text-end">
                                        <div className="d-flex justify-content-end">
                                            {canEditPosition ? (
                                                <OverlayTrigger overlay={<Tooltip>{t("Edit")}</Tooltip>}>
                                                    <Button
                                                        variant="outline-secondary"
                                                        className="outline-secondary text-dark m-1 p-0"
                                                        onClick={() =>
                                                            editPosition({
                                                                id: i.id,
                                                                name: i.name,
                                                                description: i.description,
                                                            })
                                                        }
                                                        style={{ height: "39px", width: "39px" }}
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </Button>
                                                </OverlayTrigger>
                                            ) : null}
                                            {canDeletePosition ? (
                                                <OverlayTrigger overlay={<Tooltip>{t("Delete position")}</Tooltip>}>
                                                    <Button
                                                        variant="outline-secondary"
                                                        className="outline-secondary text-dark m-1 p-0"
                                                        onClick={() => deletePosition(i)}
                                                        style={{ height: "39px", width: "39px" }}
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </Button>
                                                </OverlayTrigger>
                                            ) : null}
                                        </div>
                                    </td>
                                ) : null}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <Pagination
                    t={t}
                    pages={Number(pageCount)}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    firstItemIndex={firstItemIndex}
                    lastItemIndex={lastItemIndex}
                    totalItems={totalItems}
                />
            </div>

            <DeletePositionModal
                show={showDeletePositionModal}
                handleModal={deletePosition}
                selectedPosition={selecteedPosition}
                positions={positions}
                refetch={refetch}
                canOpenEmployeesList={canOpenEmployeesList}
                toggleShowToast={toggleShowToast}
            />
            <CreatePositionModal
                show={showPositionModal}
                handleModal={handlePositionModal}
                companyId={companyId?.user.company.id}
                refetch={refetch}
                toggleShowToast={toggleShowToast}
            />
            <EditPositionMoadal
                show={showEditPositionModal}
                handleModal={editPosition}
                companyId={companyId?.user.company.id}
                refetch={refetch}
                selectedPosition={selecteedPosition}
                toggleShowToast={toggleShowToast}
            />
            {isErrorModalOpen && (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            )}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default Positions;
